import React, { useRef } from "react";
import Select from "react-select";

import CashOutVendorField from "./CashOutVendorField";
import CashEntryAttachments, {
  AttachmentPreview
} from "./CashEntryAttachments";
import { CashEntryDateField } from "./CashEntryWorkflow";

const CashOutEntryRow = ({
  row,
  disabled = false,
  categoriesLoading = false,
  categoryOptions = [],
  paymentModeOptions = [],
  updateRow,
  onRemoveRow,
  canRemoveRow = false,
  attachmentsLoading = false,
  onAttachmentsChange,
  dateWindow = null
}) => {
  const inputRef = useRef(null);

  // Keep this check AFTER the hook.
  // Parent must supply an entry row.
  if (!row) {
    return null;
  }

  const attachments = Array.isArray(row.attachments)
    ? row.attachments
    : [];

  const changeField = (field, value) => {
    if (disabled) return;
    updateRow?.(row.id, field, value);
  };

  const changeAttachments = (files) => {
    if (disabled || attachmentsLoading) return;

    if (onAttachmentsChange) {
      onAttachmentsChange(files);
    } else {
      updateRow?.(row.id, "attachments", files);
    }
  };

  return (
    <div className="cash-out-entry-row mb-3">
      {/* Row 1: Date and Time */}
      <div className="row g-2 mb-3">
       <div className="form-group col-12">
          <CashEntryDateField
            id={`cash-out-date-${row.id}`}
            horizontal
            value={row.dateTime || ""}
            disabled={disabled}
            dateWindow={dateWindow}
            onChange={(value) =>
              changeField("dateTime", value)
            }
          />
        </div>
      </div>

      {/* Row 2: Category, Amount, Payment Mode and Vendor */}
      <div className="row g-2 align-items-end">
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label
            htmlFor={`cash-out-category-${row.id}`}
            className="form-label"
          >
            Category
          </label>

          <Select
            inputId={`cash-out-category-${row.id}`}
            options={categoryOptions}
            value={
              categoryOptions.find(
                (option) => option.value === row.categoryId
              ) || null
            }
            onChange={(selected) =>
              changeField("categoryId", selected?.value || "")
            }
            placeholder={
              categoriesLoading
                ? "Loading Categories..."
                : "Select Category"
            }
            isSearchable
            isClearable
            isLoading={categoriesLoading}
            isDisabled={disabled || categoriesLoading}
            noOptionsMessage={() =>
              "No cash out categories found"
            }
            className="erp-select"
            classNamePrefix="erp-select"
            menuPlacement="auto"
          />
        </div>

        <div className="form-group col-12 col-md-6 col-xl-3">
          <label
            htmlFor={`cash-out-amount-${row.id}`}
            className="form-label"
          >
            Amount (₹)
          </label>

          <input
            id={`cash-out-amount-${row.id}`}
            type="number"
            className="form-control"
            min="0"
            step="0.01"
            placeholder="Amount"
            value={row.amount ?? ""}
            disabled={disabled}
            onWheel={(event) => event.currentTarget.blur()}
            onChange={(event) =>
              changeField("amount", event.target.value)
            }
          />
        </div>

        <div className="form-group col-12 col-md-6 col-xl-3">
          <label
            htmlFor={`cash-out-payment-mode-${row.id}`}
            className="form-label"
          >
            Payment Mode
          </label>

          <Select
            inputId={`cash-out-payment-mode-${row.id}`}
            options={paymentModeOptions}
            value={
              paymentModeOptions.find(
                (option) => option.value === row.paymentMode
              ) || null
            }
            onChange={(selected) =>
              changeField("paymentMode", selected?.value || "")
            }
            placeholder="Select Mode"
            isDisabled={disabled}
            isSearchable={false}
            className="erp-select"
            classNamePrefix="erp-select"
            menuPlacement="auto"
          />
        </div>

        <div className="form-group col-12 col-md-6 col-xl-3">
          <label
            htmlFor={`cash-out-vendor-${row.id}`}
            className="form-label"
          >
            Vendor Name
          </label>

          <CashOutVendorField
            rowId={row.id}
            value={row.vendorId}
            disabled={disabled}
            onChange={(vendorId) =>
              changeField("vendorId", vendorId)
            }
          />
        </div>
      </div>

      {/* Row 3: Voucher, Remark and Attachments */}
      <div className="row g-2 align-items-start mt-1">
        <div className="form-group col-12 col-md-4 col-xl-3">
          <label
            htmlFor={`cash-out-voucher-${row.id}`}
            className="form-label"
          >
          Voucher No / Transaction ID (Optional)
          </label>

          <input
            id={`cash-out-voucher-${row.id}`}
            type="text"
            className="form-control"
            placeholder="Voucher No / Transaction ID (Optional)"
            value={row.voucherNo || ""}
            disabled={disabled}
            onChange={(event) =>
              changeField("voucherNo", event.target.value)
            }
          />
        </div>

        <div className="form-group col-12 col-md-8 col-xl-4">
          <label
            htmlFor={`cash-out-remark-${row.id}`}
            className="form-label"
          >
            Remark
          </label>

          <input
            id={`cash-out-remark-${row.id}`}
            type="text"
            className="form-control"
            placeholder="Enter remark"
            value={row.remark || ""}
            disabled={disabled}
            onChange={(event) =>
              changeField("remark", event.target.value)
            }
          />
        </div>

        <div className="col-12 col-xl-5">
          <CashEntryAttachments
            inputId={`cash-out-attachments-${row.id}`}
            inputRef={inputRef}
            attachments={attachments}
            disabled={disabled}
            processing={attachmentsLoading}
            showPreviews={false}
            onChange={changeAttachments}
            onRemoveRow={onRemoveRow}
            canRemoveRow={canRemoveRow}
          />
        </div>
      </div>

      {/* Attachment previews */}
      {attachments.length > 0 && (
        <div className="row g-2 mt-1">
          {attachments.map((file, index) => (
            <div
              key={`${file.name}-${file.lastModified}-${index}`}
              className="col-12 col-md-6"
            >
              <AttachmentPreview
                file={file}
                disabled={disabled || attachmentsLoading}
                onRemove={() =>
                  changeAttachments(
                    attachments.filter(
                      (_, fileIndex) => fileIndex !== index
                    )
                  )
                }
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CashOutEntryRow;